import React, { createContext, useContext, useState, useRef } from "react";

const NotificationContext = createContext(null);

let idCounter = 0;

export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  // Plain function refs — NOT callbacks in deps arrays. Store on ref so
  // consumers can call without causing re-renders.
  const apiRef = useRef({});

  apiRef.current.dismiss = (id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts((list) => list.filter((t) => t.id !== id));
  };

  apiRef.current.notify = (message, type = "info", options = {}) => {
    const id = ++idCounter;
    const duration = options.duration ?? (type === "error" ? 6000 : 4000);
    const toast = {
      id,
      type,
      message: typeof message === "string" ? message : String(message),
      title: options.title,
    };
    setToasts((list) => [...list, toast]);
    timersRef.current[id] = setTimeout(() => {
      apiRef.current.dismiss(id);
    }, duration);
    return id;
  };

  apiRef.current.success = (msg, opts) => apiRef.current.notify(msg, "success", opts);
  apiRef.current.error = (msg, opts) => apiRef.current.notify(msg, "error", opts);
  apiRef.current.info = (msg, opts) => apiRef.current.notify(msg, "info", opts);
  apiRef.current.warning = (msg, opts) => apiRef.current.notify(msg, "warning", opts);

  return (
    <NotificationContext.Provider value={{ toasts, api: apiRef.current }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be inside NotificationProvider");
  return ctx.api;
}

export function useToasts() {
  const ctx = useContext(NotificationContext);
  if (!ctx) return { toasts: [], api: {} };
  return ctx;
}
